import React, { useMemo } from 'react';
import { Box, Grid, PropTypes } from '@material-ui/core';
import * as Assets from '@assets';
import {
  CustomizedButton,
  CustomizedTypography,
  Stack
} from '@components/Customized';
import AppConfig from '@config';
import { useStyles } from './ReceivedQuotationNormalForm';
import {
  CapaQuotationDetailedMaterialType,
  CapaQuotationDetailedPcbAssemblyItem,
  CapaQuotationDetailedPcbFabricationItem,
  CapaQuotationDetailedQuotationItem
} from '@generated/graphql';
import { requestEnumToString } from '@utils/enumToString';
import { Download } from '@carbon/icons-react';
import { colorSet } from '@components/Provider';
import useFiles from '@hooks/useFiles';

interface QuotationViewPcbItemsTableProps {
  projectName: string;
  items: (
    | CapaQuotationDetailedPcbFabricationItem
    | CapaQuotationDetailedPcbAssemblyItem
    | CapaQuotationDetailedQuotationItem
  )[];
  quotationId?: string;
  shouldRenderDownloadButton?: boolean;
}

const QuotationViewPcbItemsTable = ({
  projectName,
  items,
  quotationId,
  shouldRenderDownloadButton
}: QuotationViewPcbItemsTableProps) => {
  const classes = useStyles();
  const { onDownloadPdf } = useFiles();

  const totalPrice = useMemo(() => {
    return items?.reduce(
      (acc, cur) => acc + (cur.cost ? Number(cur.cost) : 0),
      0
    );
  }, [items]);

  // pcb 타입이 아닌 노말일 경우 ''
  const itemType =
    items?.[0].__typename === 'CAPAQuotationDetailedPcbAssemblyItem'
      ? '조립'
      : items?.[0].__typename === 'CAPAQuotationDetailedPcbFabricationItem'
      ? '제작'
      : '';

  return (
    <>
      <Stack alignItems="center" justifyContent="space-between">
        <CustomizedTypography variant="h4" fontWeight="500" gutterBottoms={16}>
          {itemType} 견적정보
        </CustomizedTypography>
        {shouldRenderDownloadButton && (
          <CustomizedButton
            style={{
              paddingRight: 0
            }}
            variant="text"
            color="dark"
            startIcon={<Download color={colorSet.gray900} size={20} />}
            onClick={() => {
              onDownloadPdf('quotation', quotationId ?? '', projectName ?? '');
            }}
          >
            견적서 다운로드
          </CustomizedButton>
        )}
      </Stack>
      <Box className={classes.tableRoot}>
        <Grid container className={classes.tableHeader} wrap="nowrap">
          {[
            { title: '항목', width: '448px', align: 'left' },
            { title: '금액', width: '152px', align: 'right' }
          ]?.map((v, i) => (
            <Box key={i} maxWidth={v.width} width="100%">
              <CustomizedTypography
                align={v.align as PropTypes.Alignment}
                variant="subtitle2"
                color="textSecondary"
              >
                {v.title}
              </CustomizedTypography>
            </Box>
          ))}
        </Grid>

        {items?.map((item, i) => {
          return (
            <Grid container key={i} className={classes.tableData} wrap="nowrap">
              <Grid item>
                <CustomizedTypography
                  variant="body2"
                  color="textSecondary"
                  className="index"
                >
                  {i + 1}
                </CustomizedTypography>
                {itemType !== '조립' && !item.fileId?.includes(projectName) && (
                  <img
                    alt="thumbnail"
                    src={
                      item.fileId
                        ? AppConfig.fileApiUrl +
                          `/cloud/` +
                          item.fileId +
                          '.png'
                        : Assets.DefaultThumbnail
                    }
                    onError={(e) => {
                      e.currentTarget.src = Assets.DefaultThumbnail;
                    }}
                    className="thumbnail"
                  />
                )}
                <Box marginLeft={2} maxWidth={'calc(100% - 105px)'}>
                  <CustomizedTypography
                    variant="body1"
                    color="textPrimary"
                    gutterBottoms={4}
                    className="fileName"
                  >
                    {item.fileName}
                  </CustomizedTypography>

                  {itemType === '' && (
                    <CustomizedTypography
                      className="material"
                      variant="body2"
                      color="textSecondary"
                    >
                      {`재료: ${requestEnumToString(
                        (item as CapaQuotationDetailedQuotationItem)
                          .material as CapaQuotationDetailedMaterialType
                      )}`}
                    </CustomizedTypography>
                  )}

                  <CustomizedTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottoms={2}
                  >
                    {`수량: ${item.quantity.toLocaleString('kr')}개`}
                  </CustomizedTypography>

                  <CustomizedTypography variant="body2" color="textSecondary">
                    {`단가: ${Number(item.cost / item.quantity).toLocaleString(
                      'kr'
                    )}원`}
                  </CustomizedTypography>
                </Box>
              </Grid>

              <Box maxWidth={94}>
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                  align="right"
                  style={{ wordBreak: 'break-all' }}
                >
                  {'총 ' + Number(item.cost).toLocaleString('kr') + ' 원'}
                </CustomizedTypography>
              </Box>
            </Grid>
          );
        })}
        {itemType !== '' && (
          <div className="file-table-subtotal">
            <CustomizedTypography variant="body1" color="textSecondary">
              {itemType} 총액
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textSecondary"
            >{`총 ${totalPrice.toLocaleString('kr')} 원`}</CustomizedTypography>
          </div>
        )}
      </Box>
    </>
  );
};

export default QuotationViewPcbItemsTable;
